import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { Handshake, Add, UnfoldMore, Save, TrendingUp, History, FileDownload } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { conveniosAPI, obrasSocialesAPI, tiposEstudiosAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import { exportConveniosExcel } from '../utils/excelConvenios';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;
const EMPTY = {
  idObraSocial: '', idTipoEstudio: '', codigo: '', costo: '',
  copago: '', porcentajeAumento: '', fechaActualizacion: '', observacion: '',
};

const GrillaConvenios = () => {
  const [convenios, setConvenios] = useState([]);
  const [obrasSociales, setObrasSociales] = useState([]);
  const [tiposEstudios, setTiposEstudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [filtroOS, setFiltroOS] = useState('');
  const [page, setPage] = useState(1);

  // Modal ABM
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  // Modal Aumento General
  const [mostrarAumento, setMostrarAumento] = useState(false);
  const [formAumento, setFormAumento] = useState({ idObraSocial: '', porcentaje: '', fechaActualizacion: '' });

  // Modal Log
  const [mostrarLog, setMostrarLog] = useState(null); // { id, titulo }
  const [log, setLog] = useState([]);

  const { sortedData, requestSort, getSortIcon } = useSortableTable(convenios);

  const cargar = useCallback(async (idOS) => {
    setLoading(true);
    try {
      const [cRes, osRes, teRes] = await Promise.all([
        conveniosAPI.getConvenios(idOS || null),
        obrasSocialesAPI.getObrasSociales(),
        tiposEstudiosAPI.getTiposEstudios(),
      ]);
      setConvenios(cRes.data || []);
      setObrasSociales(osRes.data || []);
      setTiposEstudios(teRes.data || []);
    } catch {
      setToast({ message: 'Error al cargar los datos.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = sortedData.filter(c =>
    !filtroOS || String(c.idObraSocial) === filtroOS
  );
  const totalPages = Math.ceil(filtrados.length / PAGE_SIZE);
  const paginados = filtrados.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const esDuplicado = () => {
    if (!form.idObraSocial || !form.idTipoEstudio) return false;
    if (editando) return false;
    return convenios.some(c =>
      String(c.idObraSocial) === form.idObraSocial &&
      String(c.idTipoEstudio) === form.idTipoEstudio
    );
  };

  const handleNuevo = () => { setEditando(null); setForm(EMPTY); setMostrarForm(true); };
  const handleEditar = (c) => {
    setEditando(c);
    setForm({
      idObraSocial: String(c.idObraSocial), idTipoEstudio: String(c.idTipoEstudio),
      codigo: c.codigo || '', costo: c.costo != null ? String(c.costo) : '',
      copago: c.copago != null ? String(c.copago) : '', porcentajeAumento: '',
      fechaActualizacion: c.fechaActualizacion ? c.fechaActualizacion.substring(0, 10) : '',
      observacion: c.observacion || '',
    });
    setMostrarForm(true);
  };
  const handleCancelar = () => { setMostrarForm(false); setEditando(null); setForm(EMPTY); };

  const handleGuardar = async (e) => {
    e.preventDefault();
    if (esDuplicado()) {
      setToast({ message: 'Ya existe un convenio para esa obra social y tipo de estudio.', type: 'error' });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        idObraSocial: Number(form.idObraSocial),
        idTipoEstudio: Number(form.idTipoEstudio),
        codigo: form.codigo.trim(),
        costo: form.costo ? Number(form.costo) : null,
        copago: form.copago ? Number(form.copago) : null,
        porcentajeAumento: form.porcentajeAumento ? Number(form.porcentajeAumento) : null,
        fechaActualizacion: form.fechaActualizacion || null,
        observacion: form.observacion.trim() || null,
      };
      if (editando) {
        await conveniosAPI.actualizar(editando.idObraSocialTipoEstudio, payload);
        setToast({ message: 'Convenio actualizado con éxito.', type: 'success' });
      } else {
        await conveniosAPI.guardar(payload);
        setToast({ message: 'Convenio guardado con éxito.', type: 'success' });
      }
      handleCancelar();
      cargar(filtroOS);
    } catch (err) {
      setToast({ message: err.response?.status === 409 ? 'Ya existe un convenio para esa obra social y tipo de estudio.' : 'Error al guardar el convenio.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await conveniosAPI.eliminar(confirmEliminar);
      setToast({ message: 'Convenio eliminado con éxito.', type: 'success' });
      cargar(filtroOS);
    } catch {
      setToast({ message: 'Error al eliminar el convenio.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  const handleAumentoGeneral = async (e, exportar = false) => {
    e.preventDefault();
    setLoading(true);
    try {
      await conveniosAPI.aumentoGeneral({
        idObraSocial: Number(formAumento.idObraSocial),
        porcentaje: Number(formAumento.porcentaje),
        fechaActualizacion: formAumento.fechaActualizacion,
      });
      const idOS = formAumento.idObraSocial;
      setMostrarAumento(false);
      setFormAumento({ idObraSocial: '', porcentaje: '', fechaActualizacion: '' });
      const res = await conveniosAPI.getConvenios(idOS);
      const datos = res.data || [];
      setConvenios(prev => {
        const otros = prev.filter(c => String(c.idObraSocial) !== String(idOS));
        return [...otros, ...datos];
      });
      if (exportar) {
        const osNombre = obrasSociales.find(o => String(o.idObraSocial) === String(idOS))?.obraSocial;
        exportConveniosExcel(datos, osNombre);
        setToast({ message: 'Aumento aplicado y Excel generado.', type: 'success' });
      } else {
        setToast({ message: 'Aumento aplicado con éxito.', type: 'success' });
      }
      cargar(filtroOS);
    } catch {
      setToast({ message: 'Error al aplicar el aumento.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerLog = async (c) => {
    try {
      const res = await conveniosAPI.getLog(c.idObraSocialTipoEstudio);
      setLog(res.data || []);
      setMostrarLog({ id: c.idObraSocialTipoEstudio, titulo: `${c.tipoEstudio} - ${c.obraSocial}` });
    } catch {
      setToast({ message: 'Error al cargar el historial.', type: 'error' });
    }
  };

  const handleExportarExcel = () => {
    const osNombre = obrasSociales.find(o => String(o.idObraSocial) === filtroOS)?.obraSocial;
    exportConveniosExcel(filtrados, osNombre);
    setToast({ message: 'Excel generado con éxito.', type: 'success' });
  };

  const f = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));
  const duplicado = mostrarForm && esDuplicado();

  return (
    <div className="grilla-turnos">
      <style>{`
        .modal-convenios-log .modal-content { max-width: none !important; width: 100% !important; }
        .modal-convenios-log { max-width: 1000px !important; width: 90vw !important; }
      `}</style>
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Handshake sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Convenios de Obras Sociales
          </h2>
          <span className="page-subtitle">Administración de convenios por tipo de estudio</span>
        </div>

        {/* Modal ABM */}
        <Modal show={mostrarForm} onHide={handleCancelar} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editando ? 'Editar Convenio' : 'Nuevo Convenio'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body>
              <div className="row">
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>* Obra Social</Form.Label>
                  <Form.Select key={`os-${form.idObraSocial}`} value={form.idObraSocial} onChange={f('idObraSocial')} required isInvalid={duplicado}>
                    <option value="">Seleccione...</option>
                    {obrasSociales.map(o => <option key={o.idObraSocial} value={o.idObraSocial}>{o.obraSocial}</option>)}
                  </Form.Select>
                  {duplicado && <Form.Control.Feedback type="invalid">Ya existe ese convenio.</Form.Control.Feedback>}
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>* Tipo de Estudio</Form.Label>
                  <Form.Select key={`te-${form.idTipoEstudio}`} value={form.idTipoEstudio} onChange={f('idTipoEstudio')} required isInvalid={duplicado}>
                    <option value="">Seleccione...</option>
                    {tiposEstudios.map(t => <option key={t.idTipoEstudio} value={t.idTipoEstudio}>{t.tipoEstudio}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="col-md-4 mb-3">
                  <Form.Label>* Código</Form.Label>
                  <Form.Control type="text" maxLength={10} placeholder="Código..." value={form.codigo} onChange={f('codigo')} required autoFocus />
                </Form.Group>
                <Form.Group className="col-md-4 mb-3">
                  <Form.Label>* Costo</Form.Label>
                  <Form.Control type="number" step="0.01" placeholder="$$$..." value={form.costo} onChange={f('costo')} required />
                </Form.Group>
                <Form.Group className="col-md-4 mb-3">
                  <Form.Label>Copago</Form.Label>
                  <Form.Control type="number" step="0.01" placeholder="$$$..." value={form.copago} onChange={f('copago')} />
                </Form.Group>
                {editando && (
                  <Form.Group className="col-md-4 mb-3">
                    <Form.Label>% Aumento (opcional)</Form.Label>
                    <Form.Control type="number" step="0.01" min="0" max="100" placeholder="##" value={form.porcentajeAumento} onChange={f('porcentajeAumento')} />
                  </Form.Group>
                )}
                <Form.Group className="col-md-4 mb-3">
                  <Form.Label>* Fecha de Actualización</Form.Label>
                  <Form.Control type="date" value={form.fechaActualizacion} onChange={f('fechaActualizacion')} required />
                </Form.Group>
                <Form.Group className="col-md-12 mb-3">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control type="text" maxLength={100} placeholder="Observaciones..." value={form.observacion} onChange={f('observacion')} />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelar} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || duplicado}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {editando ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal Aumento General */}
        <Modal show={mostrarAumento} onHide={() => setMostrarAumento(false)}>
          <Modal.Header closeButton>
            <Modal.Title><TrendingUp sx={{ mr: 1 }} />Aumento General</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleAumentoGeneral}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>* Obra Social</Form.Label>
                <Form.Select value={formAumento.idObraSocial} onChange={e => setFormAumento(p => ({ ...p, idObraSocial: e.target.value }))} required>
                  <option value="">Seleccione...</option>
                  {obrasSociales.map(o => <option key={o.idObraSocial} value={o.idObraSocial}>{o.obraSocial}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Porcentaje (%)</Form.Label>
                <Form.Control type="number" step="0.01" min="0" max="100" placeholder="##" value={formAumento.porcentaje} onChange={e => setFormAumento(p => ({ ...p, porcentaje: e.target.value }))} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Fecha de Actualización</Form.Label>
                <Form.Control type="date" value={formAumento.fechaActualizacion} onChange={e => setFormAumento(p => ({ ...p, fechaActualizacion: e.target.value }))} required />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setMostrarAumento(false)} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="warning" disabled={loading}>
                <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} /> Aplicar Aumento
              </Button>
              <Button variant="success" disabled={loading || !formAumento.idObraSocial || !formAumento.porcentaje || !formAumento.fechaActualizacion} onClick={(e) => handleAumentoGeneral(e, true)}>
                <FileDownload sx={{ fontSize: 16, mr: 0.5 }} /> Aplicar y Exportar Excel
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal Log */}
        <Modal show={!!mostrarLog} onHide={() => setMostrarLog(null)} size="xl" dialogClassName="modal-convenios-log">
          <Modal.Header closeButton>
            <Modal.Title><History sx={{ mr: 1 }} />Historial — {mostrarLog?.titulo}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <DataTable style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Usuario', 'Fecha', 'Código', 'Costo', 'Convenio', 'Última Act.', 'Observación'].map(h => (
                    <th key={h} style={{ padding: '0.5rem', borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {log.length === 0 ? (
                  <tr><td colSpan="7" style={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>Sin historial</td></tr>
                ) : log.map((l, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '0.4rem' }}>{l.usuario}</td>
                    <td style={{ padding: '0.4rem' }}>{l.fAcion ? new Date(l.fAcion).toLocaleString('es-AR') : ''}</td>
                    <td style={{ padding: '0.4rem' }}>{l.codigo}</td>
                    <td style={{ padding: '0.4rem' }}>{l.costo}</td>
                    <td style={{ padding: '0.4rem' }}>{l.fConvenio ? new Date(l.fConvenio).toLocaleDateString('es-AR') : ''}</td>
                    <td style={{ padding: '0.4rem' }}>{l.fModif ? new Date(l.fModif).toLocaleDateString('es-AR') : ''}</td>
                    <td style={{ padding: '0.4rem' }}>{l.observacion}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setMostrarLog(null)}>Cerrar</Button>
          </Modal.Footer>
        </Modal>

        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Filtros de Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${filtroOS ? 'filter-active' : ''}`}>
                <label className="filter-label">Obra Social</label>
                <select className="filter-input" value={filtroOS} onChange={e => { setFiltroOS(e.target.value); setPage(1); }}>
                  <option value="">Todas</option>
                  {obrasSociales.map(o => <option key={o.idObraSocial} value={o.idObraSocial}>{o.obraSocial}</option>)}
                </select>
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={() => { cargar(filtroOS); setPage(1); }} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => { setFiltroOS(''); setPage(1); cargar(); }}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{filtrados.length} convenios encontrados</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={handleExportarExcel} style={{ marginRight: '0.5rem' }} disabled={filtrados.length === 0}>
                <FileDownload sx={{ fontSize: 18, mr: 0.5 }} /> Exportar Excel
              </Button>
              <Button variant="warning" size="sm" onClick={() => setMostrarAumento(true)} style={{ marginRight: '0.5rem' }}>
                <TrendingUp sx={{ fontSize: 18, mr: 0.5 }} /> Aumento General
              </Button>
              <Button variant="success" size="sm" onClick={handleNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo Convenio
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('obraSocial')} style={{ cursor: 'pointer' }}>Obra Social {getSortIcon('obraSocial') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th onClick={() => requestSort('codigo')} style={{ cursor: 'pointer' }}>Código {getSortIcon('codigo') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th onClick={() => requestSort('tipoEstudio')} style={{ cursor: 'pointer' }}>Estudio {getSortIcon('tipoEstudio') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th onClick={() => requestSort('costo')} style={{ cursor: 'pointer' }}>Costo {getSortIcon('costo') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th onClick={() => requestSort('copago')} style={{ cursor: 'pointer' }}>Copago {getSortIcon('copago') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th onClick={() => requestSort('fConvenio')} style={{ cursor: 'pointer' }}>Convenio {getSortIcon('fConvenio') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th onClick={() => requestSort('fModif')} style={{ cursor: 'pointer' }}>Última Act. {getSortIcon('fModif') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}</th>
                  <th>Observación</th>
                  <th className="action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 ? (
                  <tr><td colSpan="9" className="empty-state">{loading ? 'Cargando...' : 'No hay convenios para mostrar'}</td></tr>
                ) : paginados.map(c => (
                  <tr key={c.idObraSocialTipoEstudio}>
                    <td>{c.obraSocial}</td>
                    <td>{c.codigo}</td>
                    <td>{c.tipoEstudio}</td>
                    <td>{c.costo}</td>
                    <td>{c.copago}</td>
                    <td>{c.fConvenio ? new Date(c.fConvenio).toLocaleDateString('es-AR') : ''}</td>
                    <td>{c.fModif ? new Date(c.fModif).toLocaleDateString('es-AR') : ''}</td>
                    <td>{c.observacion}</td>
                    <td className="action-cell">
                      <div style={{ display: 'flex', gap: '0.3rem' }}>
                        <Button variant="outline-secondary" size="sm" onClick={() => handleVerLog(c)} title="Historial"><History sx={{ fontSize: 14 }} /></Button>
                        <Button variant="contained" size="sm" sx={{ bgcolor: '#3b82f6 !important', color: '#fff !important', '&:hover': { bgcolor: '#2563eb !important' } }} onClick={() => handleEditar(c)}>Editar</Button>
                        <Button variant="outline-danger" size="sm" onClick={() => setConfirmEliminar(c.idObraSocialTipoEstudio)}>Eliminar</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar convenio"
        message="¿Estás seguro que querés eliminar este convenio?"
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaConvenios;
